import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Utensils, Check, RefreshCw } from 'lucide-react';
import {
  CUSTOMER_VISIBLE_STEPS,
  statusColor,
  type RestaurantOrderStatus,
} from '@kss/api';
import type { DineRootState } from './dineSlice';
import { useDineOrders, type DineOrderTicket } from './useDineOrders';

/**
 * Customer-facing Orders tab.
 *
 *  - Orders sub-tab: one card per placed round with a horizontal
 *    Placed → Accepted → Cooking → Ready → Served progress timeline
 *    that polls the live status every 15s.
 *  - Item List sub-tab: aggregated line items across all rounds so
 *    the customer can sanity-check what's still coming.
 *
 * If the session hasn't placed anything yet we render the
 * "No Orders Yet" empty state with a "Start Ordering" CTA.
 */
export default function DineOrders() {
  const { tenantCode = '', qrToken = '' } = useParams<{ tenantCode: string; qrToken: string }>();
  const placed = useSelector((s: DineRootState) => s.dine.placedOrderIds);
  const cart = useSelector((s: DineRootState) => s.dine.lines);
  const { tickets, loading, refresh } = useDineOrders();
  const [tab, setTab] = useState<'orders' | 'items'>('orders');

  const itemsAcross = aggregateLines(tickets, cart);

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-2 border-b border-border bg-surface-card">
        <TabButton
          active={tab === 'orders'}
          label="Orders"
          count={placed.length}
          onClick={() => setTab('orders')}
        />
        <TabButton
          active={tab === 'items'}
          label="Item List"
          count={itemsAcross.length}
          onClick={() => setTab('items')}
        />
      </div>

      {tab === 'orders' && (
        <div className="flex items-center justify-end gap-2 px-4 py-2 text-[11px] text-content-muted">
          {loading ? 'refreshing…' : 'live'}
          <button onClick={() => void refresh()} className="inline-flex items-center gap-1 text-primary">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      )}

      {tab === 'orders' && placed.length === 0 && (
        <EmptyState
          title="No Orders Yet"
          subtitle="You haven’t ordered anything yet. Place your first order."
          ctaTo={`/dine/${tenantCode}/t/${qrToken}/menu`}
        />
      )}

      {tab === 'orders' && tickets.length > 0 && (
        <ul className="divide-y divide-border bg-surface-card">
          {tickets.map((t, idx) => (
            <li key={t.id} className="px-4 py-3">
              <OrderTicketCard idx={idx + 1} ticket={t} />
            </li>
          ))}
        </ul>
      )}

      {tab === 'items' && itemsAcross.length === 0 && (
        <EmptyState
          title="Nothing in your order"
          subtitle="Pick a few items from the menu first."
          ctaTo={`/dine/${tenantCode}/t/${qrToken}/menu`}
        />
      )}

      {tab === 'items' && itemsAcross.length > 0 && (
        <ul className="divide-y divide-border bg-surface-card">
          {itemsAcross.map((l) => (
            <li key={l.name} className="flex items-center justify-between px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-content-primary">{l.name}</div>
                <div className="text-[12px] text-content-muted">Qty {l.quantity}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */

function OrderTicketCard({ idx, ticket }: { idx: number; ticket: DineOrderTicket }) {
  const c = statusColor(ticket.status);
  const reachedIdx = CUSTOMER_VISIBLE_STEPS.indexOf(ticket.status);
  return (
    <div>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm font-semibold text-content-primary">Order #{idx}</div>
          <div className="text-[11px] text-content-muted">
            {ticket.placedAt ? new Date(ticket.placedAt).toLocaleTimeString() : 'just now'} · {ticket.code}
          </div>
        </div>
        <span
          className="rounded-full px-2.5 py-1 text-[11px] font-medium"
          style={{ background: c.bg, color: c.fg }}
        >
          {c.label}
        </span>
      </div>

      {/* Timeline */}
      <ol className="mt-3 flex items-center">
        {CUSTOMER_VISIBLE_STEPS.map((step, i) => {
          const reached = reachedIdx >= 0 && i <= reachedIdx;
          const isCurrent = i === reachedIdx;
          return (
            <li key={step} className="flex flex-1 items-center last:flex-none">
              <div className="flex flex-col items-center">
                <div
                  className={`grid h-6 w-6 place-items-center rounded-full text-[11px] font-semibold ${
                    reached ? 'bg-primary text-primary-foreground' : 'bg-border text-content-muted'
                  } ${isCurrent ? 'ring-2 ring-primary-light' : ''}`}
                >
                  {reached ? <Check size={12} /> : i + 1}
                </div>
                <span className={`mt-1 text-[10px] ${reached ? 'text-content-primary' : 'text-content-muted'}`}>
                  {step}
                </span>
              </div>
              {i < CUSTOMER_VISIBLE_STEPS.length - 1 && (
                <div
                  className={`mx-1 -mt-4 h-[2px] flex-1 ${i < reachedIdx ? 'bg-primary' : 'bg-border'}`}
                />
              )}
            </li>
          );
        })}
      </ol>

      {ticket.items && ticket.items.length > 0 && (
        <ul className="mt-3 space-y-0.5 text-[13px] text-content-secondary">
          {ticket.items.slice(0, 4).map((it, i) => (
            <li key={i} className="flex">
              <span className="mr-2 text-content-muted">{it.quantity}×</span>
              <span className="flex-1">{it.name}</span>
              {it.note && <span className="text-[11px] italic text-content-muted">“{it.note}”</span>}
            </li>
          ))}
          {ticket.items.length > 4 && (
            <li className="text-[11px] text-content-muted">…and {ticket.items.length - 4} more</li>
          )}
        </ul>
      )}

      <CalloutForStatus status={ticket.status} />
    </div>
  );
}

function CalloutForStatus({ status }: { status: RestaurantOrderStatus }) {
  if (status === 'Ready') {
    return (
      <div className="mt-3 rounded-md bg-success-light px-3 py-2 text-[12px] font-medium text-success">
        Your food is ready — a server will bring it shortly.
      </div>
    );
  }
  if (status === 'Cooking') {
    return (
      <div className="mt-3 rounded-md bg-warning-light px-3 py-2 text-[12px] text-warning">
        Chef is preparing your food now.
      </div>
    );
  }
  if (status === 'Accepted') {
    return (
      <div className="mt-3 rounded-md bg-primary-light px-3 py-2 text-[12px] text-primary">
        Kitchen has accepted your order.
      </div>
    );
  }
  return null;
}

/* -------------------------------------------------------------------- */

function aggregateLines(
  tickets: DineOrderTicket[],
  cart: Array<{ name: string; qty: number }>,
): Array<{ name: string; quantity: number }> {
  const map = new Map<string, number>();
  for (const t of tickets) for (const i of t.items ?? []) {
    map.set(i.name, (map.get(i.name) ?? 0) + i.quantity);
  }
  for (const l of cart) map.set(l.name, (map.get(l.name) ?? 0) + l.qty);
  return Array.from(map.entries()).map(([name, quantity]) => ({ name, quantity }));
}

function TabButton({
  active, label, count, onClick,
}: { active: boolean; label: string; count: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-center gap-2 py-3 text-sm font-medium ${
        active ? 'border-b-2 border-primary text-primary' : 'text-content-muted'
      }`}
    >
      <span>{label}</span>
      <span
        className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] ${
          active ? 'bg-primary-light text-primary' : 'bg-surface-app text-content-muted'
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function EmptyState({
  title, subtitle, ctaTo,
}: { title: string; subtitle: string; ctaTo: string }) {
  return (
    <div className="flex flex-col items-center px-6 pt-12 text-center">
      <div className="grid h-24 w-24 place-items-center rounded-full bg-primary-light text-primary">
        <Utensils size={36} />
      </div>
      <h2 className="mt-4 text-base font-semibold text-content-primary">{title}</h2>
      <p className="mt-1 max-w-xs text-sm text-content-muted">{subtitle}</p>
      <Link
        to={ctaTo}
        className="mt-6 inline-flex h-11 items-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground"
      >
        Start Ordering
      </Link>
    </div>
  );
}
